/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useContext, useState } from 'react';
import { apiService } from '../services/api';

interface EmailStats {
  total_sent: number;
  total_delivered: number;
  total_bounced: number;
  total_opened: number;
  total_clicked: number;
  delivery_rate: number;
  open_rate: number;
  click_rate: number;
}

interface Campaign {
  id: string;
  name: string;
  description?: string;
  subject: string;
  status: 'draft' | 'scheduled' | 'sending' | 'sent';
  recipients: number;
  sent: number;
  opened: number;
  clicked: number;
  createdAt: Date;
  scheduledAt?: Date;
}

interface EmailTemplate {
  id: string;
  userId: string;
  name: string;
  subject: string;
  content: string;
  type: 'transactional' | 'marketing';
  createdAt: Date;
  editorState?: string;
}

interface RepositoryTemplate {
  templateId: string;
  name: string;
  description: string;
  subject?: string;
  content?: string;
  category: string;
  tags: string[];
  userId?: string;
  usageCount?: number;
  createdAt?: number;
  updatedAt?: number | string;
}

interface EmailContextType {
  stats: EmailStats;
  campaigns: Campaign[];
  templates: EmailTemplate[];
  createCampaign: (campaign: Omit<Campaign, 'id' | 'createdAt'>, userId: string) => void;
  updateCampaign: (campaignId: string, campaign: Partial<Campaign>) => Promise<void>;
  deleteCampaign: (campaignId: string) => Promise<void>;
  createTemplate: (template: Omit<EmailTemplate, 'id' | 'createdAt' | 'userId'>, userId: string) => Promise<EmailTemplate>;
  updateTemplate: (templateId: string, template: Omit<EmailTemplate, 'id' | 'createdAt' | 'userId'>, userId: string) => Promise<EmailTemplate>;
  deleteTemplate: (templateId: string) => Promise<void>;
  createAITemplate: (templateData: any) => Promise<RepositoryTemplate>;
  getRepositoryTemplates: (params?: any) => Promise<RepositoryTemplate[]>;
  cloneRepositoryTemplate: (templateId: string) => Promise<EmailTemplate>;
  getRepositoryTemplate: (templateId: string) => Promise<RepositoryTemplate>;
  loadCampaigns: () => Promise<void>;
  loadTemplates: () => Promise<void>;
  loadStats: () => Promise<void>;
  loading: boolean;
  error: string | null;
}

const EmailContext = createContext<EmailContextType | undefined>(undefined);

export function EmailProvider({ children }: { children: React.ReactNode }) {
  const [stats, setStats] = useState<EmailStats>({
    total_sent: 12547,
    total_delivered: 12231,
    total_bounced: 316,
    total_opened: 8654,
    total_clicked: 2156,
    delivery_rate: 97.5,
    open_rate: 68.9,
    click_rate: 17.2
  });

  const [campaigns, setCampaigns] = useState<Campaign[]>([
    // Initial empty state - will be loaded from API
  ]);

  const [templates, setTemplates] = useState<EmailTemplate[]>([
    // Initial empty state - will be loaded from API
  ]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createCampaign = async (campaign: Omit<Campaign, 'id' | 'createdAt'>) => {
    try {
      setLoading(true);
      setError(null);
      
      const newCampaign = await apiService.createCampaign(campaign); // userId will come from auth header
      const campaignWithCorrectId = {
        ...newCampaign,
        id: newCampaign.campaignId || newCampaign.id,
        createdAt: new Date(newCampaign.createdAt)
      };
      setCampaigns(prev => [campaignWithCorrectId, ...prev]);
      
      return campaignWithCorrectId;
    } catch (error: any) {
      setError(error.message || 'Failed to create campaign');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateCampaign = async (campaignId: string, campaign: Partial<Campaign>) => {
    try {
      if (!campaignId) {
        throw new Error('Campaign ID is required for updates');
      }
      
      setLoading(true);
      setError(null);
      
      const updatedCampaign = await apiService.updateCampaign(campaignId, campaign);
      
      const campaignWithCorrectId = {
        ...updatedCampaign,
        id: campaignId,
        createdAt: new Date(updatedCampaign.createdAt)
      };
      
      setCampaigns(prev => prev.map(c => 
        c.id === campaignId ? campaignWithCorrectId : c
      ));
      
      return campaignWithCorrectId;
    } catch (error: any) {
      setError(error.message || 'Failed to update campaign');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deleteCampaign = async (campaignId: string) => {
    try {
      if (!campaignId) {
        throw new Error('Campaign ID is required for deletion');
      }
      
      setLoading(true);
      setError(null);
      
      await apiService.deleteCampaign(campaignId);
      
      setCampaigns(prev => prev.filter(c => c.id !== campaignId));
    } catch (error: any) {
      setError(error.message || 'Failed to delete campaign');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const createTemplate = async (template: Omit<EmailTemplate, 'id' | 'createdAt' | 'userId'>) => {
    try {
      setLoading(true);
      setError(null);
      
      // Explicitly pick only allowed fields to satisfy strict backend validation
      const cleanTemplate = {
        name: (template as any).name,
        subject: (template as any).subject,
        type: (template as any).type,
        content: (template as any).content,
        editorState: (template as any).editorState
      };
      
      const response = await apiService.createTemplate(cleanTemplate);
      const newTemplate = {
        ...response,
        id: response.templateId || response.id,
        createdAt: new Date(response.createdAt)
      };
      setTemplates(prev => [newTemplate, ...prev]);
      
      return newTemplate;
    } catch (error: any) {
      setError(error.message || 'Failed to create template');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateTemplate = async (templateId: string, template: Omit<EmailTemplate, 'id' | 'createdAt' | 'userId'>) => {
    try {
      if (!templateId) {
        throw new Error('Template ID is required for updates');
      }
      
      setLoading(true);
      setError(null);

      // Explicitly pick only allowed fields to satisfy strict backend validation
      const cleanTemplate = {
        name: (template as any).name,
        subject: (template as any).subject,
        type: (template as any).type,
        content: (template as any).content,
        editorState: (template as any).editorState
      };
      
      const updatedTemplate = await apiService.updateTemplate(templateId, cleanTemplate);
      
      const templateWithCorrectId = {
        ...updatedTemplate,
        id: templateId,
        createdAt: new Date(updatedTemplate.createdAt)
      };
      
      setTemplates(prev => prev.map(t => 
        t.id === templateId ? templateWithCorrectId : t
      ));
      
      return templateWithCorrectId;
    } catch (error: any) {
      setError(error.message || 'Failed to update template');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deleteTemplate = async (templateId: string) => {
    try {
      if (!templateId) {
        throw new Error('Template ID is required for deletion');
      }
      
      setLoading(true);
      setError(null);
      
      await apiService.deleteTemplate(templateId);
      
      setTemplates(prev => prev.filter(t => t.id !== templateId));
    } catch (error: any) {
      setError(error.message || 'Failed to delete template');
      throw error;
    } finally {
      setLoading(false);
    }
  };


  const loadCampaigns = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.getCampaigns();
      const campaignsWithCorrectId = (response.campaigns || []).map((campaign: any) => ({
        ...campaign,
        id: campaign.campaignId || campaign.id,
        createdAt: new Date(campaign.createdAt),
        scheduledAt: campaign.scheduledAt ? new Date(campaign.scheduledAt) : undefined
      }));
      setCampaigns(campaignsWithCorrectId);
    } catch (error: any) {
      setError(error.message || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.getTemplates();
      const templatesWithCorrectId = (response.templates || []).map((template: any) => ({
        ...template,
        id: template.templateId || template.id,
        createdAt: new Date(template.createdAt)
      }));
      setTemplates(templatesWithCorrectId);
    } catch (error: any) {
      setError(error.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  // Template Repository functions
  const createAITemplate = async (templateData: any): Promise<RepositoryTemplate> => {
    try {
      setLoading(true);
      setError(null);
      
      const newTemplate = await apiService.createAITemplate(templateData);
      return newTemplate;
    } catch (error: any) {
      setError(error.message || 'Failed to create AI template');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getRepositoryTemplates = async (params?: any): Promise<RepositoryTemplate[]> => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.getRepositoryTemplates(params);
      return response.templates || [];
    } catch (error: any) {
      setError(error.message || 'Failed to load repository templates');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const cloneRepositoryTemplate = async (templateId: string): Promise<EmailTemplate> => {
    try {
      setLoading(true);
      setError(null);
      
      const clonedTemplate = await apiService.cloneRepositoryTemplate(templateId);
      const templateWithCorrectId = {
        ...clonedTemplate,
        id: clonedTemplate.templateId || clonedTemplate.id,
        createdAt: new Date(clonedTemplate.createdAt)
      };
      
      setTemplates(prev => [templateWithCorrectId, ...prev]);
      return templateWithCorrectId;
    } catch (error: any) {
      setError(error.message || 'Failed to clone template');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getRepositoryTemplate = async (templateId: string): Promise<RepositoryTemplate> => {
    try {
      setLoading(true);
      setError(null);
      
      const template = await apiService.getRepositoryTemplate(templateId);
      return template;
    } catch (error: any) {
      setError(error.message || 'Failed to load repository template');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const statsData = await apiService.getStats();
      setStats(statsData);
    } catch (error: any) {
      setError(error.message || 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  return (
    <EmailContext.Provider value={{
      stats,
      campaigns,
      templates,
      createCampaign,
      updateCampaign,
      deleteCampaign,
      createTemplate,
      updateTemplate,
      deleteTemplate,
      createAITemplate,
      getRepositoryTemplates,
      cloneRepositoryTemplate,
      getRepositoryTemplate,
      loadCampaigns,
      loadTemplates,
      loadStats,
      loading,
      error
    }}>
      {children}
    </EmailContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useEmail() {
  const context = useContext(EmailContext);
  if (context === undefined) {
    throw new Error('useEmail must be used within an EmailProvider');
  }
  return context;
}